import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getProjectHome } from '../lib/api';
import { badgesFromHome, BADGES_MAX_AGE_MS } from '../lib/shellBadges';

// NAV-FE-1 — the ONE answer to "which project is the user in?". The shell
// (sidebar, project switcher, breadcrumb) reads it, and so does every page that
// reads `lastProjectId`. In order, the first that answers wins:
//   1. the URL path   /projects/:id/...
//   2. the URL query  ?project_id=
//   3. the URL query  ?project=          (the inspection pages' spelling)
//   4. the project this user last worked in, remembered in localStorage under
//      `cqa.lastProject:<user id>` — only the id, written on every change
//   5. the user's ONLY project, when they have exactly one
//   6. null — with several projects and nothing remembered, nothing is guessed.
// Why it is persisted now: every link in the sidebar carries the project, and a
// reload on a page whose URL names none (/asset-qa-table, /profile) must not
// leave the bar pointing nowhere. Why the key carries the user id: two accounts
// sharing one browser must never inherit each other's project — and
// AuthContext's logout removes the key as well. The old fallback (the FIRST key
// of the access map) is gone: for anyone with more than one project it was an
// arbitrary pick presented as a choice.
// `lastProjectId` / `setLastProjectId` keep their names and their meaning: the
// value is the current project, so the pages that read them do not change.
export const lastProjectStorageKey = (userId) => `cqa.lastProject:${userId}`;

const readRemembered = (userId) => {
  if (!userId) return null;
  try {
    return window.localStorage.getItem(lastProjectStorageKey(userId)) || null;
  } catch {
    return null;
  }
};

const writeRemembered = (userId, projectId) => {
  if (!userId) return;
  try {
    if (projectId) window.localStorage.setItem(lastProjectStorageKey(userId), projectId);
    else window.localStorage.removeItem(lastProjectStorageKey(userId));
  } catch {
    // Storage blocked (private window, site data cleared): the URL still
    // carries the project, only the reload fallback is lost.
  }
};

/** Steps 1-3: the project the URL names, or null. */
export const projectIdFromUrl = (pathname, search) => {
  const match = pathname.match(/\/projects\/([^/]+)/);
  if (match) return match[1];
  const params = new URLSearchParams(search);
  return params.get('project_id') || params.get('project') || null;
};

const ProjectContext = createContext({
  currentProjectId: null,
  rememberedProjectId: null,
  lastProjectId: null,
  setLastProjectId: () => {},
  projectsVersion: 0,
  bumpProjects: () => {},
  badges: null,
  refreshBadges: () => {},
  setBadgesFromHome: () => {},
});

export function ProjectProvider({ children }) {
  const location = useLocation();
  const { user, projectAccessesMap } = useAuth();
  const userId = user?.id || null;
  const [rememberedProjectId, setRememberedProjectId] = useState(() => readRemembered(userId));

  // A sign-in, a sign-out or a different account reads its own memory.
  useEffect(() => {
    setRememberedProjectId(readRemembered(userId));
  }, [userId]);

  const fromUrl = projectIdFromUrl(location.pathname, location.search);

  // Every project a URL names becomes the one remembered.
  useEffect(() => {
    if (!fromUrl || !userId) return;
    writeRemembered(userId, fromUrl);
    setRememberedProjectId(fromUrl);
  }, [fromUrl, userId]);

  const onlyProjectId = projectAccessesMap && projectAccessesMap.size === 1
    ? [...projectAccessesMap.keys()][0]
    : null;
  const currentProjectId = fromUrl || rememberedProjectId || onlyProjectId || null;

  const setLastProjectId = useCallback((projectId) => {
    writeRemembered(userId, projectId || null);
    setRememberedProjectId(projectId || null);
  }, [userId]);

  // SHELL-REFRESH-1 — a change to a project the shell lists (Project settings'
  // Save) bumps this; the Layout rereads its project list when it moves.
  const [projectsVersion, setProjectsVersion] = useState(0);
  const bumpProjects = useCallback(() => setProjectsVersion((v) => v + 1), []);

  // NAV-FE-6 — the sidebar's count pills (docs/design/home.md §4). They live
  // here, above the routes, because every page mounts its own Layout. The Home
  // page pushes the answer it read; on any other page the shell asks the Home
  // itself, at most once a minute per project. `badges` names its project: a
  // reader checks `badges.projectId` before trusting the counts.
  const [badges, setBadges] = useState(null);
  const lastReadRef = useRef({ projectId: null, at: 0 });
  const inFlightRef = useRef(null);

  const setBadgesFromHome = useCallback((projectId, home) => {
    const counts = badgesFromHome(home);
    if (!projectId || !counts) return;
    const at = Date.now();
    lastReadRef.current = { projectId, at };
    setBadges({ projectId, counts, at });
  }, []);

  // Silent on failure: no toast, no error state — the pills simply do not show.
  const refreshBadges = useCallback((projectId) => {
    if (!projectId || inFlightRef.current === projectId) return;
    const last = lastReadRef.current;
    if (last.projectId === projectId && Date.now() - last.at < BADGES_MAX_AGE_MS) return;
    lastReadRef.current = { projectId, at: Date.now() };
    inFlightRef.current = projectId;
    Promise.resolve()
      .then(() => getProjectHome(projectId))
      // A late answer for a project the user has since left is dropped.
      .then((res) => {
        if (lastReadRef.current.projectId === projectId) setBadgesFromHome(projectId, res?.data);
      })
      .catch(() => {})
      .finally(() => {
        if (inFlightRef.current === projectId) inFlightRef.current = null;
      });
  }, [setBadgesFromHome]);

  return (
    <ProjectContext.Provider value={{
      currentProjectId,
      rememberedProjectId,
      lastProjectId: currentProjectId,
      setLastProjectId,
      projectsVersion,
      bumpProjects,
      badges,
      refreshBadges,
      setBadgesFromHome,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
